import { addMonths, type DateArg, getWeeksInMonth, isAfter, isBefore, subMonths } from "date-fns";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { StyleSheet, View, type ViewProps } from "react-native";
import { Calendar, CalendarContext, type CalendarProps, type DateData } from "react-native-calendars";
import { UpdateSources } from "react-native-calendars/src/expandableCalendar/commons";
import { Button } from "~/components/Button";
import { DAY_ITEM_HEIGHT, DayItem } from "~/components/layout/calendar/DayItem";
import { Title } from "~/components/layout/text";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";

import { Space } from "~/constants/sizing";
import { useJournal } from "~/providers/JournalProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { formatDateString, formatMonthName, getMonth } from "~/util/dates";

export const DATE_SELECTOR_HEIGHT = Space.xl;
export const DATE_CALENDAR_HEADER_HEIGHT = 37;
export const DATE_CALENDAR_ROW_MARGIN = 7;
export const DATE_CALENDAR_LINE_HEIGHT = DATE_CALENDAR_ROW_MARGIN + DAY_ITEM_HEIGHT + DATE_CALENDAR_ROW_MARGIN;

export type MonthlyCalendarProps = {
    onCalendarHeightChange?: (height: number) => void;
    toolbarProps?: ViewProps;
    nativeHeader?: boolean;
} & CalendarProps;

type CalendarRenderProps = {
    height: number;
} & Omit<CalendarProps, "dayComponent">;

export const MonthlyCalendar = ({
    onCalendarHeightChange,
    toolbarProps,
    nativeHeader,
    ...calendarProps
}: MonthlyCalendarProps) => {
    const { colours } = useTheme();
    const now = formatDateString(new Date());
    const { date, setDate } = useContext(CalendarContext);
    const { journalsForMonth, marked } = useJournal();
    const { showSixWeeks, hideExtraDays } = calendarProps;
    const lines = useMemo(
        () => (showSixWeeks && !hideExtraDays ? 6 : getWeeksInMonth(date)),
        [date, showSixWeeks, hideExtraDays],
    );
    const height = useMemo(() => DATE_CALENDAR_HEADER_HEIGHT + lines * DATE_CALENDAR_LINE_HEIGHT, [lines]);

    useEffect(() => {
        onCalendarHeightChange?.(height);
    }, [onCalendarHeightChange, height]);

    const month = getMonth(date);

    const adjacent_months = useMemo(
        () => ({
            next: addMonths(month, 1),
            previous: subMonths(month, 1),
        }),
        [month],
    );

    const onMonthChange = ({ dateString }: DateData) => {
        const entries = journalsForMonth(dateString);
        const all_dates = Object.keys(entries);
        const keys = Object.entries(entries)
            .filter(([, journals]) => journals.length > 0)
            .map(([key]) => key);

        const now = formatDateString(new Date());

        if (all_dates.includes(date)) {
            return;
        }

        if (keys.includes(now)) {
            changeDate(now, UpdateSources.PROP_UPDATE);
        } else if (keys.length === 0 || keys.includes(dateString)) {
            changeDate(dateString, UpdateSources.PROP_UPDATE);
        } else if (isBefore(dateString, keys[keys.length - 1])) {
            changeDate(keys[keys.length - 1], UpdateSources.PROP_UPDATE);
        } else if (isAfter(dateString, keys[0])) {
            changeDate(keys[0], UpdateSources.PROP_UPDATE);
        } else {
            changeDate(dateString, UpdateSources.PROP_UPDATE);
        }
    };
    const changeDate = (to: DateArg<Date> & string, source?: UpdateSources) => {
        setDate(formatDateString(to), source ?? UpdateSources.PROP_UPDATE);
    };

    const calendarDateChanged = (to: DateData) => changeDate(to.dateString, UpdateSources.DAY_PRESS);

    const header: Pick<CalendarProps, "renderArrow" | "renderHeader" | "hideArrows"> = useMemo(() => {
        if (nativeHeader) {
            return {};
        }
        const render_nothing = () => <Nothing />;

        return { renderArrow: render_nothing, renderHeader: render_nothing, hideArrows: true };
    }, [nativeHeader]);

    const RenderCalendar = useCallback(
        ({ height, theme, style, ...calendarProps }: CalendarRenderProps) => (
            <Calendar
                style={[{ height }, style]}
                dayComponent={DayItem}
                theme={{
                    calendarBackground: colours.background.primary,
                    textSectionTitleColor: colours.text.tertiary,
                    ...theme,
                }}
                {...calendarProps}
            />
        ),
        [colours],
    );

    return (
        <>
            <Only not when={nativeHeader}>
                <View {...toolbarProps} style={[styles.toolbar, toolbarProps?.style]}>
                    <Title size="xl" style={styles.title}>
                        {formatMonthName(date)}
                    </Title>
                    <Button
                        icon="chevron-left"
                        size="sm"
                        variant="quiet"
                        onPress={() => changeDate(adjacent_months.previous)}
                    />
                    <Button
                        title="Today"
                        size="sm"
                        variant="quiet"
                        onPress={() => changeDate(now, UpdateSources.TODAY_PRESS)}
                    />
                    <Button
                        icon="chevron-right"
                        size="sm"
                        variant="quiet"
                        onPress={() => changeDate(adjacent_months.next)}
                    />
                </View>
            </Only>
            <RenderCalendar
                height={height}
                markedDates={marked}
                initialDate={date}
                onMonthChange={onMonthChange}
                onDayPress={calendarDateChanged}
                {...header}
                {...calendarProps}
            />
        </>
    );
};

const styles = StyleSheet.create({
    toolbar: {
        flexDirection: "row",
        alignItems: "center",
        gap: Space.sm,
        paddingLeft: Space.sm,
        paddingRight: Space.sm,
        height: DATE_SELECTOR_HEIGHT,
    },
    title: {
        flexGrow: 1,
    },
    journals: {
        flex: 1,
        paddingLeft: Space.lg,
        paddingRight: Space.lg,
    },
});
